<template>
  <div class="container-fluid">
    <div class="page-title">
      <h3>Comisión de pagos</h3>
    </div>

    <div class="card">
      <div class="card-header pb-0">
        <h4>Configuración de comisiones</h4>
        <p class="text-muted mb-0">Gestiona markup, comisión y service fee en un solo lugar.</p>
      </div>
      <div class="card-body">
        <div v-if="errorMsg" class="alert alert-danger mb-3">{{ errorMsg }}</div>
        <div v-if="successMsg" class="alert alert-success mb-3">{{ successMsg }}</div>

        <div class="alert alert-info">
          <strong>Información importante:</strong><br />
          - Esta configuración se usa para calcular cobros con Stripe Connect.<br />
          - La comisión cobrada se toma del campo <strong>Comisión de plataforma</strong>.<br />
          - El simulador estima reparto final restando fee Stripe.
        </div>

        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
        </div>

        <template v-else>
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">Markup para app</label>
              <input
                v-model.number="form.appMarkupRate"
                type="number"
                min="0"
                max="1"
                step="0.01"
                class="form-control"
              />
              <small class="text-muted">
                Porcentaje de incremento para precio app (ej. 0.20 = 20%)<br />
                Valor actual: {{ (Number(form.appMarkupRate || 0) * 100).toFixed(2) }}%
              </small>
            </div>
            <div class="col-md-6">
              <label class="form-label">Comisión de plataforma</label>
              <input
                v-model.number="form.commissionRate"
                type="number"
                min="0"
                max="1"
                step="0.01"
                class="form-control"
              />
              <small class="text-muted">
                Porcentaje de comisión sobre precio base (ej. 0.20 = 20%)<br />
                Valor actual: {{ (Number(form.commissionRate || 0) * 100).toFixed(2) }}%
              </small>
            </div>
            <div class="col-md-6">
              <label class="form-label">Service fee (MXN)</label>
              <input
                v-model.number="form.serviceFeeMxn"
                type="number"
                min="0"
                step="0.01"
                class="form-control"
              />
              <small class="text-muted">
                Cargo fijo cobrado al cliente por uso de la plataforma<br />
                Valor actual: {{ formatMxn(form.serviceFeeMxn) }}
              </small>
            </div>
          </div>

          <div class="d-flex justify-content-end gap-2 mt-3">
            <button class="btn btn-outline-secondary" :disabled="saving" @click="fetchActive">
              Recargar
            </button>
            <button class="btn btn-primary" :disabled="saving" @click="save">
              {{ saving ? 'Guardando...' : 'Guardar configuración' }}
            </button>
          </div>

          <hr class="my-4" />

          <h5 class="mb-3">Simulador de reparto</h5>
          <div class="row g-3">
            <div class="col-md-4">
              <label class="form-label">Precio base del vendor (MXN)</label>
              <input v-model.number="sim.amountMxn" type="number" min="0" step="0.01" class="form-control" />
            </div>
            <div class="col-md-4">
              <label class="form-label">Fee Stripe (%) estimado</label>
              <input v-model.number="sim.stripeFeePercent" type="number" min="0" max="100" step="0.01" class="form-control" />
            </div>
            <div class="col-md-4">
              <label class="form-label">Fee Stripe fijo (MXN) estimado</label>
              <input v-model.number="sim.stripeFeeFixedMxn" type="number" min="0" step="0.01" class="form-control" />
            </div>
          </div>

          <div class="row mt-3 g-3">
            <div class="col-md-3">
              <div class="p-3 border rounded h-100">
                <div class="small text-muted">Comisión plataforma (bruta)</div>
                <div class="fw-semibold">{{ formatMxn(result.platformGross) }}</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="p-3 border rounded h-100">
                <div class="small text-muted">Comisión Stripe estimada</div>
                <div class="fw-semibold">{{ formatMxn(result.stripeFee) }}</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="p-3 border rounded h-100">
                <div class="small text-muted">Neto plataforma</div>
                <div class="fw-semibold">{{ formatMxn(result.platformNet) }}</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="p-3 border rounded h-100">
                <div class="small text-muted">Le toca al viajero</div>
                <div class="fw-semibold">{{ formatMxn(result.travelerNet) }}</div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { paymentCommissionSettingsApi } from '@/api/payment-commission-settings';

export default {
  name: 'ComisionPagos',
  data() {
    return {
      loading: false,
      saving: false,
      errorMsg: '',
      successMsg: '',
      form: {
        appMarkupRate: 0.2,
        commissionRate: 0.2,
        serviceFeeMxn: 12,
      },
      sim: {
        amountMxn: 100,
        stripeFeePercent: 4.65,
        stripeFeeFixedMxn: 0,
      },
    };
  },
  computed: {
    result() {
      const amount = Number(this.sim.amountMxn || 0); // base vendor
      const markup = amount * Number(this.form.appMarkupRate || 0);
      const serviceFee = Number(this.form.serviceFeeMxn || 0);
      const appPrice = amount + markup + serviceFee;
      const platformGross = Math.min(
        appPrice,
        appPrice * Number(this.form.commissionRate || 0),
      );
      const stripeFee =
        appPrice * (Number(this.sim.stripeFeePercent || 0) / 100) +
        Number(this.sim.stripeFeeFixedMxn || 0);
      const platformNet = Math.max(0, platformGross - stripeFee);
      const travelerNet = Math.max(0, appPrice - platformGross);
      return { platformGross, stripeFee, platformNet, travelerNet, appPrice };
    },
  },
  mounted() {
    this.fetchActive();
  },
  methods: {
    async fetchActive() {
      this.loading = true;
      this.errorMsg = '';
      this.successMsg = '';
      try {
        const { data } = await paymentCommissionSettingsApi.getActive();
        if (data) this.form = { ...this.form, ...data };
      } catch (e) {
        this.errorMsg = e.response?.data?.message || 'No se pudo cargar la configuración.';
      } finally {
        this.loading = false;
      }
    },
    async save() {
      this.saving = true;
      this.errorMsg = '';
      this.successMsg = '';
      try {
        const payload = {
          appMarkupRate: Number(this.form.appMarkupRate || 0),
          commissionRate: Number(this.form.commissionRate || 0),
          serviceFeeMxn: Number(this.form.serviceFeeMxn || 0),
        };
        await paymentCommissionSettingsApi.updateActive(payload);
        await this.fetchActive();
        this.successMsg = 'Configuración guardada. Se aplicará en nuevos paquetes.';
      } catch (e) {
        const msg = e.response?.data?.message || 'No se pudo guardar la configuración.';
        this.errorMsg = Array.isArray(msg) ? msg.join(' ') : msg;
      } finally {
        this.saving = false;
      }
    },
    formatMxn(amount) {
      const n = Number(amount) || 0;
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
      }).format(n);
    },
  },
};
</script>

